import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import Badge from '@mui/material/Badge';
import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import { styled } from '@mui/material/styles';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import TruncatedText from "app/shared-components/layout/TruncatedText";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import {Box} from "@mui/system";
import {useLocation} from "react-router-dom";

const StyledListItem = styled(ListItem)(({ theme, active }) => ({
  color: 'inherit!important',
  textDecoration: 'none!important',
  height: 30,
  width: '100%',
  borderRadius: 20,
  paddingLeft: 16,
  paddingRight: 16,
  marginBottom: 8,
  fontWeight: 500,
  '&.active': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, .05)!important'
        : 'rgba(255, 255, 255, .1)!important',
    pointerEvents: 'none',
    '& .list-item-icon': {
      color: theme.palette.secondary.main,
    },
  },
  '& .list-item-icon': {
    marginRight: 16,
  },
}));

function SidebarListItem({ link, icon, text, unread, children, onClick, expanded, registerPath }) {
    const hasChildren = children && React.Children.count(children) > 0;
    const location = useLocation(); // Import useLocation from react-router-dom
    const [childPaths, setChildPaths] = useState(new Set());

    const registerChildPath = useCallback((path) => {
        setChildPaths(prev => new Set(prev).add(path));
    }, []);

    const isChildActive = useMemo(() => {
        if (childPaths.size === 0) return false;
        return Array.from(childPaths).some(childPath =>
            location.pathname.startsWith(childPath)
        );
    }, [childPaths, location.pathname]);

    // Use both expanded prop and isChildActive for initial state
    const [open, setOpen] = useState(!!expanded || isChildActive);

    // Keep parent expanded if child is active
    useEffect(() => {
        if (isChildActive) {
            setOpen(true);
        }
    }, [isChildActive]);

    useEffect(() => {
        registerPath?.(link);
    }, [link, registerPath]);

    const handleClick = (event) => {
        if (hasChildren) {
            event.preventDefault();
            setOpen(!open);
        }
        if (onClick) {
            onClick(event);
        }
    };

    // Clone children and inject the register function
    const enhancedChildren = React.Children.map(children, child => {
        const out= React.cloneElement(child, {
            registerPath: registerChildPath
        });
        return out;
    });

    return (
        <>
            <StyledListItem
                button
                component={hasChildren ? 'div' : NavLinkAdapter}
                to={!hasChildren ? link : undefined}
                onClick={handleClick}
                end
                activeClassName="active"
                key="main"
                data-testid={`sidebar-${text.toLowerCase().replace(/\s+/g, '-')}`}
                sx={{
                    '&.active': {
                        pointerEvents: 'auto !important'
                    },
                    '& > *': {
                        pointerEvents: 'auto !important'
                    }
                }}
            >
                <ListItemIcon className="min-w-40">
                    <Badge color="secondary" variant="dot" invisible={!unread}>
                        <FuseSvgIcon>{icon}</FuseSvgIcon>
                    </Badge>
                </ListItemIcon>
                <TruncatedText text={text} />
                {hasChildren && (
                    <ListItemIcon sx={{ minWidth: 'auto' }}>
                        <FuseSvgIcon>
                            {open ? 'heroicons-solid:chevron-down' : 'heroicons-solid:chevron-right'}
                        </FuseSvgIcon>
                    </ListItemIcon>
                )}
            </StyledListItem>
            {hasChildren && (
                <Collapse in={open} timeout="auto" >
                    <List component="div" disablePadding>
                        <Box sx={{ pl: 3 }}>{enhancedChildren}</Box>
                    </List>
                </Collapse>
            )}
        </>
    );
}


export default SidebarListItem;
